import React, {ComponentProps} from "react";
import {useTranslation} from "react-i18next";
import {AnonymousResult, EducationSessionRecordState} from "../../../schema";

import Table from "../../../ui/table";

import cn from "classnames";
import classes from "./AnonymousSessionsTable.module.css";
import {formatDateTime, formatDuration} from "../../../time";
import {QueryData} from "../../../scenes/account/anonymous-sessions.graphql";
import {CheckmarkIcon, CrossIcon, PlayIcon, StarIcon} from "../../../ui/icons";
import {gql} from "@apollo/client";
import RecordLink from "../../RecordLink";
import Link from "../../../ui/link";
import SessionActionsList from "../../results/SessionActionsList";
import DetailedResults from "./DetailedResults";

export type Props = Omit<ComponentProps<typeof Table>, "children"> & {
  sessions: QueryData["paginatedList"]["items"];
  onPlayButtonClick: (event: React.MouseEvent) => void;
};

export default function AnonymousSessionsTable({sessions, onPlayButtonClick, ...props}: Props) {
  const {t} = useTranslation();

  return (
    <div className={cn(classes.root)}>
      <Table className={cn(classes.table)} {...props}>
        <Table.Head>
          <Table.Row>
            <Table.Cell>{t("components.AnonymousSessionsTable.uuid")}</Table.Cell>
            <Table.Cell />
            <Table.Cell>{t("components.AnonymousSessionsTable.passed")}</Table.Cell>
            <Table.Cell>{t("components.AnonymousSessionsTable.score")}</Table.Cell>
            <Table.Cell>{t("components.AnonymousSessionsTable.OS")}</Table.Cell>
            <Table.Cell>{t("components.AnonymousSessionsTable.challenge")}</Table.Cell>
            <Table.Cell>{t("components.AnonymousSessionsTable.project")}</Table.Cell>
            <Table.Cell>{t("components.AnonymousSessionsTable.time")}</Table.Cell>
            <Table.Cell>{t("components.AnonymousSessionsTable.startedAt")}</Table.Cell>
            <Table.Cell>{t("components.AnonymousSessionsTable.feedbackScore")}</Table.Cell>
            <Table.Cell>{t("components.AnonymousSessionsTable.feedbackText")}</Table.Cell>
            <Table.Cell>{t("components.AnonymousSessionsTable.detailed")}</Table.Cell>
            <Table.Cell>{t("components.AnonymousSessionsTable.record")}</Table.Cell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {sessions.map((session: AnonymousResult) => (
            <Table.Row key={session.uuid}>
              <Table.Cell>
                <span className={classes.groupName}>{session.uuid}</span>
              </Table.Cell>
              <Table.Cell></Table.Cell>
              <Table.Cell>
                {session.passed ? (
                  <CheckmarkIcon className={cn(classes.icon, classes.passed)} />
                ) : (
                  <CrossIcon className={cn(classes.icon, classes.failed)} />
                )}
              </Table.Cell>
              <Table.Cell>{session.score ? session.score / 10 : 0} </Table.Cell>
              <Table.Cell>{session.OS} </Table.Cell>
              <Table.Cell>{session.challenge.name} </Table.Cell>
              <Table.Cell>{session.project !== "None" ? session.project : ""}</Table.Cell>
              <Table.Cell>{formatDuration(session.time)}</Table.Cell>
              <Table.Cell>{formatDateTime(session.startedAt)}</Table.Cell>
              <Table.Cell>
                {session.feedbackScore ? (
                  <span>
                    {" "}
                    {session.feedbackScore}/5 <StarIcon className={classes.filledStar} />
                  </span>
                ) : (
                  t("components.AnonymousSessionsTable.notRated")
                )}
              </Table.Cell>
              <Table.Cell>{session.feedbackText}</Table.Cell>
              <Table.Cell>
                <DetailedResults challengeId={session.challenge.id} session={session} />
              </Table.Cell>

              <Table.Cell>
                <RecordLink
                  className={classes.recordButton}
                  as={Link}
                  data-id={session.record}
                  disabled={session.recordState !== EducationSessionRecordState.READY}
                  onClick={onPlayButtonClick}
                  state={session.recordState}
                  textVariant="short"
                >
                  {session.recordState === EducationSessionRecordState.READY && (
                    <PlayIcon className={classes.playIcon} />
                  )}
                </RecordLink>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

AnonymousSessionsTable.fragments = {
  anonymousSessions: gql`
    fragment AnonymousSessions on AnonymousResult {
      uuid
      challenge {
        id
        name
      }
      project
      passed
      score
      OS
      feedbackScore
      feedbackText
      startedAt
      finishedAt
      isContentMatches
      finishedScenariosIds
      parametricResults {
        parameterName
        rawSum
        percentage
      }
      actionResults {
        ...SessionActionsListActionResult
      }
      finishedScenariosIds
      record
      recordState
      time
    }
    ${SessionActionsList.fragments.actionsResults}
  `,
};
